/* dev-sticky-cta — interactive behavior for the sticky negotiate-pricing
   modal. Defines a `<dev-sticky-cta>` custom element that owns:

   - opening on trigger click and closing on backdrop / ESC / X
   - locking the document scroll while open (single shared lock count
     across multiple instances on the same page)
   - focus-trapping inside the modal while open and returning focus to
     the trigger on close
   - auto-opening when the section was rendered with `data-auto-open=true`
     (set when `form.posted_successfully?` is true so the merchant's
     "thanks" message is visible)

   Section / block reloads in the theme editor re-trigger
   connectedCallback automatically — no explicit shopify:section:load
   wiring needed because the custom element re-attaches each time. */

(function () {
  if (typeof customElements === 'undefined') return;

  // Shared body-scroll lock — multiple modals can be on the page (e.g. when
  // editing). Lock when count > 0, unlock when count returns to 0.
  let scrollLockCount = 0;

  function lockScroll() {
    scrollLockCount += 1;
    if (scrollLockCount === 1) {
      document.documentElement.classList.add('dev-sticky-cta-locked');
    }
  }

  function unlockScroll() {
    if (scrollLockCount > 0) scrollLockCount -= 1;
    if (scrollLockCount === 0) {
      document.documentElement.classList.remove('dev-sticky-cta-locked');
    }
  }

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class DevStickyCta extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._returnFocusTo = null;
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this._trigger = this.querySelector('[data-trigger]');
      this._modal = this.querySelector('[data-modal]');
      this._backdrop = this.querySelector('[data-backdrop]');
      this._closeBtns = this.querySelectorAll('[data-close]');

      // The trigger (FAB) is optional — on the homepage it's suppressed
      // and the modal is opened via the global click delegate below. The
      // modal element is required.
      if (!this._modal) return;

      if (this._trigger && this._trigger.dataset.devCtaBound !== '1') {
        this._trigger.dataset.devCtaBound = '1';
        this._trigger.addEventListener('click', () => this.open());
      }

      if (this._backdrop && this._backdrop.dataset.devCtaBound !== '1') {
        this._backdrop.dataset.devCtaBound = '1';
        this._backdrop.addEventListener('click', () => this.close());
      }

      this._closeBtns.forEach((btn) => {
        if (btn.dataset.devCtaBound === '1') return;
        btn.dataset.devCtaBound = '1';
        btn.addEventListener('click', () => this.close());
      });

      // Auto-open when the form was just posted successfully — Shopify
      // re-renders the page with form.posted_successfully? === true. The
      // merchant's success message is inside the modal, so opening it
      // brings the message into view without a page-anchor jump.
      if (this.dataset.autoOpen === 'true') {
        // Defer to next frame so the modal's transition starts cleanly.
        requestAnimationFrame(() => this.open({ skipFocusTrigger: true }));
      }
    }

    disconnectedCallback() {
      if (this._isOpen) {
        // Editor reload while open — clean up the global lock + listener.
        document.removeEventListener('keydown', this._onKeydown);
        unlockScroll();
        this._isOpen = false;
      }
    }

    open(options) {
      if (this._isOpen) return;
      this._isOpen = true;
      // Track where focus should return on close. Defaults to the FAB
      // when present; callers (e.g. the homepage Private Pricing card)
      // can override with options.returnFocusTo so focus returns to the
      // originating CTA when the FAB is suppressed.
      this._returnFocusTo = (options && options.returnFocusTo) || this._trigger || null;
      this.classList.add('is-open');
      if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
      if (this._modal) this._modal.setAttribute('aria-hidden', 'false');
      if (this._backdrop) this._backdrop.setAttribute('aria-hidden', 'false');

      lockScroll();
      document.addEventListener('keydown', this._onKeydown);

      // Move focus to the first focusable element inside the modal.
      // Skip when auto-opened from form-success so the merchant's message
      // is the visual focus (avoid jumping the keyboard onto the close
      // button or first input).
      if (!options || !options.skipFocusTrigger) {
        const firstFocusable = this._getFocusableElements()[0];
        if (firstFocusable) {
          // Defer one frame so the transition has started — focus during
          // a still-invisible modal flashes the outline awkwardly.
          requestAnimationFrame(() => firstFocusable.focus({ preventScroll: true }));
        }
      }
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;
      this.classList.remove('is-open');
      if (this._trigger) this._trigger.setAttribute('aria-expanded', 'false');
      if (this._modal) this._modal.setAttribute('aria-hidden', 'true');
      if (this._backdrop) this._backdrop.setAttribute('aria-hidden', 'true');

      unlockScroll();
      document.removeEventListener('keydown', this._onKeydown);

      // Return focus to whatever opened the modal (FAB, PDP callout
      // button, or homepage Private Pricing card CTA).
      const target = this._returnFocusTo;
      this._returnFocusTo = null;
      if (target && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    }

    _onKeydown(e) {
      if (!this._isOpen) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        this.close();
        return;
      }

      if (e.key === 'Tab') {
        const focusables = this._getFocusableElements();
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        // If focus is outside the modal entirely, pull it back inside.
        if (!this._modal.contains(active)) {
          e.preventDefault();
          first.focus();
          return;
        }

        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    _getFocusableElements() {
      if (!this._modal) return [];
      return Array.prototype.slice
        .call(this._modal.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
    }
  }

  if (!customElements.get('dev-sticky-cta')) {
    customElements.define('dev-sticky-cta', DevStickyCta);
  }

  // Global click delegate. Two ways an element opts in to opening the
  // <dev-sticky-cta> modal:
  //
  // 1. Carries [data-dev-pdp-assistance-open] (added by the PDP callout
  //    and by dev-button when called with open_pricing_modal: true).
  // 2. Is a tel:/mailto: anchor inside the homepage Private Pricing card
  //    or any hero slide's button row. This is a safety net so the popup
  //    fires for those CTAs even if the Liquid attribute didn't ship.
  //
  // If the modal isn't on the page, the click falls through to the
  // element's native href (mailto:/tel:) so the CTA degrades gracefully.
  var FALLBACK_SELECTOR = [
    '.dev-hero-slide__buttons a[href^="tel:"]',
    '.dev-hero-slide__buttons a[href^="mailto:"]',
    '.dev-pricing__card-buttons a[href^="tel:"]',
    '.dev-pricing__card-buttons a[href^="mailto:"]'
  ].join(',');

  if (!document.documentElement.dataset.devCtaOpenBound) {
    document.documentElement.dataset.devCtaOpenBound = '1';
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var btn = e.target.closest('[data-dev-pdp-assistance-open]');
      if (!btn) btn = e.target.closest(FALLBACK_SELECTOR);
      if (!btn) return;
      var modal = document.querySelector('dev-sticky-cta');
      if (!modal || typeof modal.open !== 'function') return;
      e.preventDefault();
      modal.open({ returnFocusTo: btn });
    });
  }
})();
